/**
 * Die Bilder unter der Blase des Schmieds. (16.10.2026, shared/schmied-bilder.ts)
 *
 * Martin, 16.10., 20:16: eine Vorschau, ein Design, ein Screenshot des Schmieds steht DIREKT in seinem Chat — unter dem Text, in
 * Vorschaugröße; ein Tipp öffnet es groß (Lightbox, bei mehreren mit Blättern).
 *
 *   - Der Platz ist reserviert, BEVOR das Bild lädt: der Server liefert Breite und Höhe mit (`width`/`height` am `<img>` — der Browser
 *     rechnet daraus das Seitenverhältnis); fehlen sie, hilft `bildmass.ts` wie bei jeder Bildblase. Sonst wüchse das Bild beim
 *     Heranscrollen von 0 auf seine Höhe und der Verlauf spränge (Ticket 2873, frontend/lib/scroll-ruhe.ts schreibt dafür nichts).
 *   - `loading="lazy"`, `decoding="async"`.
 *   - Alt-Text „Bild vom Schmied“ (+ „: Beschriftung“). Die Beschriftung steht sichtbar darunter, für Bildschirmleser verborgen (sonst
 *     doppelt). Der Knopf um das Bild sagt, dass ein Tipp vergrößert.
 *   - Nur Adressen, die `bilderAus` besteht (Ablage von Konto 1) — die Seite zeigt nie eine fremde Adresse.
 *   - Die Bilder werden nie vorgelesen: der Vorlese-Knopf nimmt den Text der Blase, nicht ihren Inhalt.
 * Aussehen: frontend/style.css, `.entwickler-bilder`.
 */
import { BILD_ALT, bilderAus, type SchmiedBild } from "../../shared/schmied-bilder.js";
import { ruesteBildAus } from "./bildmass.js";
import { hatTransparenz, makeDownloadButton } from "./bild-werkzeuge.js";
import { makeShareButton } from "./bild-blase.js";

/** Baut die Bilderreihe — oder null, wenn kein gültiges Bild dabei ist. `vergroessern(bilder, index)` öffnet die Lightbox. */
export function baueSchmiedBilder(roh: unknown, vergroessern: (bilder: SchmiedBild[], index: number) => void): HTMLElement | null {
  const bilder = bilderAus(roh);
  if (!bilder.length) return null;
  const reihe = document.createElement("div");
  reihe.className = "entwickler-bilder";
  reihe.dataset.anzahl = String(bilder.length);
  bilder.forEach((b, i) => {
    const fig = document.createElement("figure");
    fig.className = "entwickler-bild";
    const alt = b.beschriftung ? `${BILD_ALT}: ${b.beschriftung}` : BILD_ALT;
    const knopf = document.createElement("button");
    knopf.type = "button";
    knopf.className = "entwickler-bild-knopf";
    knopf.setAttribute("aria-label", `${alt} – vergrößern`);
    knopf.addEventListener("click", (ev) => {
      ev.stopPropagation();
      vergroessern(bilder, i);
    });
    const img = document.createElement("img");
    img.alt = alt;
    img.loading = "lazy";
    img.decoding = "async";
    if (b.breite && b.hoehe) {
      img.width = b.breite;
      img.height = b.hoehe;
    } else {
      ruesteBildAus(img, b.url);
    }
    img.src = b.url;
    // Durchsichtiges Bild (PNG mit Alpha): abgesetzte Bildfläche, damit Umriss und Ausdehnung sichtbar sind.
    const pruefeAlpha = (): void => {
      if (img.naturalWidth > 0 && hatTransparenz(img)) knopf.dataset.alpha = "";
    };
    if (img.complete) pruefeAlpha();
    img.addEventListener("load", pruefeAlpha, { once: true });
    knopf.appendChild(img);
    fig.appendChild(knopf);
    // Feste Struktur je Bild: Bild, darunter eine Zeile — Beschriftung links, Knöpfe rechts (Raster im Blatt).
    if (b.beschriftung) {
      const cap = document.createElement("figcaption");
      cap.className = "entwickler-bild-text";
      cap.setAttribute("aria-hidden", "true");
      cap.textContent = b.beschriftung;
      fig.appendChild(cap);
    }
    const aktionen = document.createElement("div");
    aktionen.className = "entwickler-bild-aktionen";
    aktionen.append(makeShareButton({ url: b.url, title: b.beschriftung ?? BILD_ALT }), makeDownloadButton(b.url));
    fig.appendChild(aktionen);
    reihe.appendChild(fig);
  });
  return reihe;
}

/** Hängt die Bilderreihe unter den Text einer Entwickler-Blase (vor Vorlese- und Frage-Knöpfe). Ohne Bilder geschieht nichts. */
export function haengeSchmiedBilderAn(blase: HTMLElement, roh: unknown, vergroessern: (bilder: SchmiedBild[], index: number) => void): void {
  if (blase.querySelector(":scope > .entwickler-bilder")) return;
  const reihe = baueSchmiedBilder(roh, vergroessern);
  if (!reihe) return;
  const inhalt = blase.querySelector<HTMLElement>(":scope > .bubble-content");
  if (inhalt) inhalt.after(reihe);
  else blase.appendChild(reihe);
  blase.classList.add("mit-bildern");
}
